import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar } from "../ui/components/Avatar";
import { DropdownMenu } from "../ui/components/DropdownMenu";
import { IconButton } from "../ui/components/IconButton";
import { Button } from "../ui/components/Button";
import {
  FeatherActivity,
  FeatherBook,
  FeatherDollarSign,
  FeatherFileText,
  FeatherHome,
  FeatherLogOut,
  FeatherMessageSquare,
  FeatherMoreHorizontal,
  FeatherTrendingDown,
  FeatherTrendingUp,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useAuth } from "../features/auth/presentation/hooks/useAuth";
import { LOGIN_LOGO_PATH, LOGO_ALT } from "../constants/logo";
import { getCompanyProfileApi } from "../features/auth/data/api/authApi";
import { AuthMapper } from "../features/auth/data/mappers/AuthMapper";
import { storeCompany } from "../shared/data/storage/authStorage";

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  clickable: boolean;
}

const menuItems: MenuItem[] = [
  {
    path: "/client-overview",
    label: "Overview",
    icon: <FeatherHome />,
    clickable: true,
  },
  {
    path: "/client/queries",
    label: "Queries",
    icon: <FeatherMessageSquare />,
    clickable: true,
  },
  {
    path: "/revenue",
    label: "Revenue",
    icon: <FeatherTrendingUp />,
    clickable: true,
  },
  {
    path: "/expense",
    label: "Expense",
    icon: <FeatherTrendingDown />,
    clickable: true,
  },
  {
    path: "/cash-and-bank",
    label: "Cash",
    icon: <FeatherDollarSign />,
    clickable: true,
  },
  {
    path: "/client/compliance",
    label: "Tax & Compliance",
    icon: <FeatherFileText />,
    clickable: true,
  },
  {
    path: "/books/health",
    label: "Books Health",
    icon: <FeatherActivity />,
    clickable: true,
  },
  { path: "/books", label: "Books", icon: <FeatherBook />, clickable: true },
  // {
  //   path: "/cfo/dashboard",
  //   label: "CFO",
  //   icon: <FeatherBarChart2 />,
  //   clickable: true,
  // },
];

export default function ClientAppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshAuthFromStorage } = useAuth();

  // Helper function to get user initials for avatar
  const getUserInitials = (): string => {
    if (!user?.userName) return "U";
    const nameParts = user.userName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      return (
        nameParts[0][0] + nameParts[nameParts.length - 1][0]
      ).toUpperCase();
    }
    return user.userName[0].toUpperCase();
  };

  // Helper function to format role
  const formatRole = (role: string | undefined): string => {
    if (!role) return "";
    // Convert "client_view" to "Client View", "admin" to "Admin", etc.
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleRefreshFormatSettings = async () => {
    try {
      const resp = await getCompanyProfileApi();
      const company = AuthMapper.toCompanyEntity(resp.data.company);
      storeCompany(company);
      refreshAuthFromStorage();
    } catch (e) {
      // Keep silent in UI for now (no toast system here); console is enough for debug.
      console.error("Failed to refresh company profile:", e);
    }
  };

  const isActive = (path: string): boolean => {
    if (!path) return false;
    // Exact match for most routes
    if (location.pathname === path) return true;
    // Special case for /client/queries - match any path starting with /client/queries
    if (
      path === "/client/queries" &&
      location.pathname.startsWith("/client/queries")
    )
      return true;
    // Special case for /client/compliance - match any path starting with /client/compliance
    if (
      path === "/client/compliance" &&
      location.pathname.startsWith("/client/compliance")
    )
      return true;
    // Special case for /books/health - match any path starting with /books/health
    if (
      path === "/books/health" &&
      location.pathname.startsWith("/books/health")
    )
      return true;
    // Special case for /books - match /books or /books/* but not /books/health
    if (path === "/books") {
      if (location.pathname === "/books") return true;
      if (
        location.pathname.startsWith("/books/") &&
        !location.pathname.startsWith("/books/health")
      )
        return true;
    }
    // Special case for /cfo/dashboard - match any path starting with /cfo
    if (path === "/cfo/dashboard" && location.pathname.startsWith("/cfo"))
      return true;
    return false;
  };

  const handleMenuClick = (item: MenuItem) => {
    if (item.clickable && item.path) {
      navigate(item.path);
    }
  };

  const handleRaiseQueryClick = () => {
    navigate("/client/queries", { state: { raiseQuery: true } });
  };

  return (
    <div className="flex w-60 flex-none flex-col items-start justify-between self-stretch border-r border-solid border-neutral-border bg-default-background px-4 py-6 mobile:hidden">
      <div className="flex w-full flex-col items-start gap-6">
        <div className="flex w-full items-center gap-2 px-4 py-2">
          <img
            className="h-6 flex-none object-cover"
            src={LOGIN_LOGO_PATH}
            alt={LOGO_ALT}
          />
        </div>
        <div className="flex w-full flex-col items-start gap-1">
          {menuItems.map((item, index) => {
            const active = isActive(item.path);
            const isQueries = item.path === "/client/queries";

            return (
              <div
                key={index}
                className={`flex w-full items-center gap-2 rounded-md px-4 py-2 ${
                  active
                    ? "bg-brand-50"
                    : item.clickable
                      ? "cursor-pointer hover:bg-neutral-50"
                      : ""
                }`}
                onClick={() => handleMenuClick(item)}
              >
                {item.icon && (
                  <div
                    className={`text-body font-body ${
                      active
                        ? "text-brand-700"
                        : isQueries
                          ? "text-default-font"
                          : "text-subtext-color"
                    }`}
                  >
                    {item.icon}
                  </div>
                )}
                <span
                  className={`grow shrink-0 basis-0 ${
                    isQueries
                      ? "text-body-bold font-body-bold"
                      : "text-body font-body"
                  } text-default-font`}
                >
                  {item.label}
                </span>
              </div>
            );
          })}

          <div className="my-2 w-full border-t border-neutral-border" />

          {/* Matches Books Health "RaiseQuerySection" card design */}
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <span className="text-body font-body text-default-font">
              Need any clarification?
            </span>
            <Button
              variant="brand-primary"
              icon={<FeatherMessageSquare />}
              onClick={handleRaiseQueryClick}
              className="w-full"
            >
              Raise a query
            </Button>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center gap-2 px-4 py-2">
        <div className="flex grow shrink-0 basis-0 items-start gap-2">
          <Avatar image={undefined}>{getUserInitials()}</Avatar>
          <div className="flex flex-col items-start">
            <span className="text-caption-bold font-caption-bold text-default-font">
              {user?.userName || "User"}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              {formatRole(user?.role) || "Member"}
            </span>
          </div>
        </div>
        <SubframeCore.DropdownMenu.Root>
          <SubframeCore.DropdownMenu.Trigger asChild={true}>
            <IconButton
              size="small"
              icon={<FeatherMoreHorizontal />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
            />
          </SubframeCore.DropdownMenu.Trigger>
          <SubframeCore.DropdownMenu.Portal>
            <SubframeCore.DropdownMenu.Content
              side="bottom"
              align="start"
              sideOffset={4}
              asChild={true}
            >
              <DropdownMenu>
                {/* <DropdownMenu.DropdownItem icon={<FeatherUser />}>
                  Profile
                </DropdownMenu.DropdownItem>
                <DropdownMenu.DropdownItem icon={<FeatherSettings />}>
                  Settings
                </DropdownMenu.DropdownItem> */}
                <DropdownMenu.DropdownItem
                  icon={<FeatherLogOut />}
                  onClick={handleLogout}
                >
                  Log out
                </DropdownMenu.DropdownItem>
                <DropdownMenu.DropdownItem
                  icon={<FeatherMoreHorizontal />}
                  onClick={handleRefreshFormatSettings}
                >
                  Refresh format settings
                </DropdownMenu.DropdownItem>
              </DropdownMenu>
            </SubframeCore.DropdownMenu.Content>
          </SubframeCore.DropdownMenu.Portal>
        </SubframeCore.DropdownMenu.Root>
      </div>
    </div>
  );
}
